import { Users, Code, Map, Palette, BookOpen } from "lucide-react";
import MatrixRain from "@/components/MatrixRain";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import FloatingActions from "@/components/FloatingActions";
import TeamConnection from "@/components/TeamConnection";
import { team } from "@/data/gamedev-data";

const Team = () => {
  return (
    <div className="min-h-screen bg-background relative">
      <MatrixRain />
      <Navigation />

      {/* Hero Section */}
      <section className="relative py-20 pt-32 pb-16 overflow-hidden">
        <div className="absolute inset-0 cyber-gradient opacity-20"></div>
        <div className="absolute inset-0 hex-pattern"></div>
        
        {/* ASCII Art Background */}
        <div className="absolute inset-0 flex items-center justify-center opacity-30 pointer-events-none scale-150">
          <pre className="font-terminal text-xs text-primary leading-tight">
{`
  ████████╗███████╗ █████╗ ███╗   ███╗
  ╚══██╔══╝██╔════╝██╔══██╗████╗ ████║
     ██║   █████╗  ███████║██╔████╔██║
     ██║   ██╔══╝  ██╔══██║██║╚██╔╝██║
     ██║   ███████╗██║  ██║██║ ╚═╝ ██║
     ╚═╝   ╚══════╝╚═╝  ╚═╝╚═╝     ╚═╝
`}
          </pre>
        </div>
        
        <div className="container mx-auto px-4 max-w-6xl relative z-10">
          <div className="text-center mb-8">
            <p className="text-sm font-mono text-primary/50 mb-6 tracking-widest uppercase">TEAM</p>
            <h1 className="text-6xl md:text-7xl font-bold font-cyber mb-6 mt-32">
              <span className="text-accent">&lt;</span>
              <span className="neon-gradient bg-clip-text text-transparent">
                НАША_КОМАНДА
              </span>
              <span className="text-accent">/&gt;</span>
            </h1>
            <p className="font-mono text-foreground/70 text-lg mb-4">
              <span className="text-secondary">{'// '}</span>{team.length} творческих разработчика
            </p>
            <p className="font-terminal text-accent animate-pulse text-xl">
              {'> '} СОЗДАЕМ_ИГРЫ_ВМЕСТЕ {'_'}
            </p>
          </div>
        </div>
      </section>

      {/* Team Cards Section */}
      <section className="relative py-10">
        <div className="container mx-auto px-4 max-w-6xl relative z-10">

          <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
            {team.map((member) => {
              const IconComponent = 
                member.icon === "Code" ? Code :
                member.icon === "Map" ? Map :
                member.icon === "Palette" ? Palette :
                member.icon === "BookOpen" ? BookOpen : Users;

              const colorClass = 
                member.color === "primary" ? "border-primary/50 bg-gradient-to-br from-primary/20 to-primary/5" :
                member.color === "secondary" ? "border-secondary/50 bg-gradient-to-br from-secondary/20 to-secondary/5" :
                member.color === "accent" ? "border-accent/50 bg-gradient-to-br from-accent/20 to-accent/5" :
                "border-primary/50 bg-gradient-to-br from-primary/15 to-accent/5";

              const iconColorClass =
                member.color === "primary" ? "text-primary" :
                member.color === "secondary" ? "text-secondary" :
                member.color === "accent" ? "text-accent" :
                "text-primary";

              return (
                <div key={member.id} className={`cyber-card p-10 space-y-8 group transition-all duration-300 ${colorClass}`}>
                  <div className="flex items-start gap-6">
                    <div className={`p-4 rounded-sm border ${member.color === "primary" ? "border-primary" : member.color === "secondary" ? "border-secondary" : member.color === "accent" ? "border-accent" : "border-primary"} bg-background/50 transition-all duration-300`}>
                      <IconComponent className={`w-14 h-14 ${iconColorClass} transition-colors`} />
                    </div>
                    <div className="flex-1">
                      <h2 className="text-2xl font-bold font-cyber terminal-text mb-2 group-hover:text-accent transition-colors">
                        {member.name}
                      </h2>
                      <p className={`text-base font-mono ${iconColorClass} mb-4 font-bold`}>
                        {member.role}
                      </p>
                      <p className="text-foreground/80 font-mono text-base leading-relaxed">
                        {member.description}
                      </p>
                    </div>
                  </div>
                  
                  <div className="cyber-card p-6 bg-background/50 space-y-3">
                    <p className="font-mono text-xs text-primary/90 mb-3 font-bold">
                      {'>> НАВЫКИ И СПЕЦИАЛИЗАЦИЯ:'}
                    </p>
                    {member.skills.map((skill, i) => (
                      <div key={i} className="text-base font-mono text-foreground/80 flex items-start gap-2">
                        <span className="text-secondary mt-1">&gt;</span>
                        <span>{skill}</span>
                      </div>
                    ))}
                  </div>

                  <div className="flex gap-1 pt-2">
                    <div className="h-1 flex-1 bg-primary/30"></div>
                    <div className="h-1 flex-1 bg-accent/30"></div>
                    <div className="h-1 flex-1 bg-secondary/30"></div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Team Connection Component */}
          <TeamConnection />

          <div className="mt-16 cyber-card p-10 text-center space-y-6">
            <h3 className="text-3xl md:text-4xl font-bold font-cyber terminal-text">
              {'<'}<span className="text-primary">РАБОТАЕМ_ВМЕСТЕ</span>{'>'}
            </h3>
            <p className="font-mono text-foreground/80 max-w-3xl mx-auto text-base leading-relaxed">
              <span className="text-accent">&gt;</span> Каждый участник команды вносит уникальный вклад в создание игр. 
              Программирование, дизайн уровней, визуальное искусство и концепция игры — 
              все элементы объединяются в единое творческое произведение.
            </p>
            <div className="pt-4">
              <p className="font-terminal text-primary animate-pulse">
                {'> '} СОЗДАЕМ_ВМЕСТЕ {'_'}
              </p>
            </div>
          </div>
        </div>
      </section>

      <FloatingActions />
      <Footer />
    </div>
  );
};

export default Team;
